import type { UserID } from '@common/ids';
import EnvironmentConfig from '@util/config-environment';
import * as datadog from './datadog';
import {
    type ObservabilityInitOptions,
    type ObservabilityType,
    type ObservabilityCustomAction,
    type LogLevel,
    LogLevelPriorities,
} from './types';

const isLogLevel = (value: string): value is LogLevel => value in LogLevelPriorities;

// Tracks observability types that have been initialized for error reporting.
let initializedTypes = new Set<ObservabilityType>();

/**
 * Sets initializedTypes to empty. Used for testing purposes ONLY.
 */
export function resetInitializedTypes() {
    initializedTypes = new Set<ObservabilityType>();
}

/**
 * Returns the initializedTypes. Used for testing purposes ONLY.
 */
export function getInitializedTypes() {
    return initializedTypes;
}

/**
 * Initializes the specified observability platform.
 *
 * @param {ObservabilityInitOptions} type, options the type of the observability platform and initialization options.
 * @returns true if the platform was successfully initialized; otherwise, returns false.
 */
export function initObservability({ type, options }: ObservabilityInitOptions): boolean {
    let initialized = false;

    try {
        switch (type) {
            case 'datadog':
                initialized = datadog.init(options);
                if (initialized) {
                    initializedTypes.add(type);
                } else {
                    initializedTypes.delete(type);
                }
                break;
            default:
                break;
        }
    } catch (e) {
        // This is unlikely, but catching errors just in case there are any issues.
        // We don't want any observability issues to prevent using the app.
        // eslint-disable-next-line no-console
        console.error('Failed to initialize observability', e);
    }

    return initialized;
}

/**
 * Add user information to logs and observability sessions
 *
 * @param {User} user
 * @returns void
 */
export function setUser(user: { id: UserID; email?: string }): void {
    initializedTypes.forEach((type) => {
        switch (type) {
            case 'datadog':
                datadog.setUser(user);
                break;
            default:
                break;
        }
    });
}

/**
 * Remove user information from future logs and observability sessions
 *
 * @returns void
 */
export function clearUser(): void {
    initializedTypes.forEach((type) => {
        switch (type) {
            case 'datadog':
                datadog.clearUser();
                break;
            default:
                break;
        }
    });
}

export function addAction(actionName: ObservabilityCustomAction, additionalContext?: Record<string, unknown>): void {
    initializedTypes.forEach((type) => {
        switch (type) {
            case 'datadog':
                datadog.addAction(actionName, additionalContext);
                break;
            default:
                break;
        }
    });
}

export const { ACTION_NAME_ATTRIBUTE } = datadog;
/**
 * Utility for setting the RUM action name as a prop on a JSX element.
 *
 * @example
 * <div {...getActionNameAttribute('my-action-name')} />
 */
export function getActionNameAttribute(actionName: string | undefined) {
    return { [ACTION_NAME_ATTRIBUTE]: actionName || undefined };
}

/**
 * Wrap error and context for observability reporting.
 *
 * This will ensure that `error` is an instance of `Error` and that `additionalContext` is wrapped in a `gc` property.
 *
 * @param error Thrown error, will be wrapped in an instance of `Error` if needed.
 * @param additionalContext Additional context to include in the `gc` property.
 * @param message Optional message to use for the error instance if wrapping is needed.
 */
function getErrorAndContext(
    error?: Error | unknown,
    additionalContext?: Record<string, unknown>,
    message: string = 'An error occurred',
) {
    // Wrap context if needed
    let context = additionalContext && { gc: additionalContext };

    // Ensure that error is of type Error so we can get a stack trace
    const wrapError = error !== undefined && !(error instanceof Error);
    const errorInstance = wrapError ? new Error(message, { cause: error }) : error;

    // Add original error to context if not of type Error
    if (wrapError) {
        if (context) {
            context.gc.originalError = error;
        } else {
            context = { gc: { originalError: error } };
        }
    }

    // Return Error instance and wrapped context
    return { errorInstance, context };
}

/**
 * Adds an error to all initialized observability platforms.
 *
 * @param {unknown} error - The error to report.
 * @param {Record<string, unknown>} additionalContext - Optional - Additional context for the error.
 * @param {string} message - Optional - Message to use if error instance is wrapped.
 *
 * @returns nothing
 */
export function addError(error: unknown, additionalContext?: Record<string, unknown>, message?: string): void {
    const { errorInstance, context } = getErrorAndContext(error, additionalContext, message);

    initializedTypes.forEach((type) => {
        switch (type) {
            case 'datadog':
                datadog.addError({ error: errorInstance, context });
                break;
            default:
                break;
        }
    });
}

/**
 * Adds a React render error to all initialized observability platforms, with the component stack attached.
 * Only for React error boundaries (componentDidCatch); use `addError` everywhere else.
 *
 * @param {Error} error - The error caught during render.
 * @param {{ componentStack: string }} info - React error info from componentDidCatch.
 * @param {Record<string, unknown>} context - Optional - Additional context merged into the error event (e.g. boundaryName).
 *
 * @returns nothing
 */
export function addReactError(error: Error, info: { componentStack: string }, context?: Record<string, unknown>): void {
    initializedTypes.forEach((type) => {
        switch (type) {
            case 'datadog':
                datadog.addReactError(error, info, context);
                break;
            default:
                break;
        }
    });
}

/**
 * Log a message of the specified level to all initialized observability platforms.
 *
 * @param level The log level.
 * @param message The message to log.
 * @param data Optional - Additional data for the log message.
 * @param data.error Optional - Error to include in the log.
 * @param data.context Optional - Additional context to include in the log.
 */
export function log(
    level: LogLevel,
    message: string,
    data?: { error?: Error | unknown; context?: Record<string, unknown> },
): void {
    const { context, errorInstance } = getErrorAndContext(data?.error, data?.context, message);

    const logToConsole = () => {
        // Console logging allowed within observability
        // eslint-disable-next-line no-console
        console[level === 'critical' ? 'error' : level](
            message,
            // Only add extra data if it is defined
            ...[errorInstance, context].filter((data) => data),
        );
    };

    // If no types have been initialized, log to console
    if (initializedTypes.size === 0) {
        logToConsole();
    }

    const minDatadogLevel = isLogLevel(EnvironmentConfig.DATADOG_LOG_LEVEL)
        ? LogLevelPriorities[EnvironmentConfig.DATADOG_LOG_LEVEL]
        : LogLevelPriorities.info;

    initializedTypes.forEach((type) => {
        switch (type) {
            case 'datadog':
                // Log allowed levels to datadog
                if (LogLevelPriorities[level] >= minDatadogLevel) {
                    datadog.log(level, message, context, errorInstance);

                    // If in development, always log to console as well.
                    // This will cause some duplicate logs, but is done because by default
                    // logs sent to Datadog are sampled so not everything will show up.
                    if (process.env.NODE_ENV === 'development') {
                        logToConsole();
                    }
                } else {
                    // Log any other levels to console
                    logToConsole();
                }
                break;
            default:
                break;
        }
    });
}

/**
 * Sends the error to both RUM (with session context) and Logs. Use for any user-visible failure.
 *
 * @param error - The error to report.
 * @param message - Log message describing what failed.
 * @param context - `feature` scopes the error to a specific flow in Datadog. `team` overrides CODEOWNERS routing.
 */
export function reportError(
    error: unknown,
    message: string,
    context: { feature: string; team?: string } & Record<string, unknown>,
): void {
    addError(error, context, message);
    logger.error(message, { error, context });
}

function logFunction(level: LogLevel) {
    return (message: string, data?: { error?: Error | unknown; context?: Record<string, unknown> }) => {
        log(level, message, data);
    };
}

export const logger = {
    log,

    /**
     * Log a critical level message.
     *
     * @param message The message to log.
     * @param data Optional - Additional data for the log message.
     * @param data.error Optional - Error to include in the log.
     * @param data.context Optional - Additional context to include in the log.
     */
    critical: logFunction('critical'),

    /**
     * Log an error level message.
     *
     * @param message The message to log.
     * @param data Optional - Additional data for the log message.
     * @param data.error Optional - Error to include in the log.
     * @param data.context Optional - Additional context to include in the log.
     */
    error: logFunction('error'),

    /**
     * Log a warn level message.
     *
     * @param message The message to log.
     * @param data Optional - Additional data for the log message.
     * @param data.error Optional - Error to include in the log.
     * @param data.context Optional - Additional context to include in the log.
     */
    warn: logFunction('warn'),

    /**
     * Log an info level message.
     *
     * @param message The message to log.
     * @param data Optional - Additional data for the log message.
     * @param data.error Optional - Error to include in the log.
     * @param data.context Optional - Additional context to include in the log.
     */
    info: logFunction('info'),

    /**
     * Log a debug level message.
     *
     * @param message The message to log.
     * @param data Optional - Additional data for the log message.
     * @param data.error Optional - Error to include in the log.
     * @param data.context Optional - Additional context to include in the log.
     */
    debug: logFunction('debug'),
};
